import { openNextStep } from './nextStep.js';
import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { MouseSensor, TouchSensor, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { Check, ChevronRight, GripVertical, MoreHorizontal } from 'lucide-react';
import { TASK_TITLE_MAX } from '@werft/domain';
import type { LabelDto, ProjectMemberDto, SectionDto, TaskDto, TaskListDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { useRights } from '../../platform/rights.js';
import { useDraftText } from '../../platform/drafts.js';
import { ConfirmDialog } from '../../ui/Dialog.js';
import { Menu, MenuGroups, MenuItem, MenuSeparator } from '../../ui/Menu.js';
// 0.150: Zielwähler erst beim Öffnen laden
const TargetPicker = lazy(() => import('../../ui/TargetPicker.js').then((m) => ({ default: m.TargetPicker })));
// 0.151: Inhalt der Zeilenmenüs getrennt geladen (Startpaket F13); im Leerlauf vorgeladen
export const loadRowMenus = () => import('./RowMenus.js');
const TaskRowMenuItems = lazy(() => loadRowMenus().then((m) => ({ default: m.TaskRowMenuItems })));
import { isPreview, TaskMeta, type DetailOpen, type PickerKind } from './Pickers.js';
import { DoneMark } from './DoneControl.js';
import { AccentBar, TaskCover } from './Cover.js';
import { useShown } from './display.js';
import { latestDone, linger, release, trackDone, useLingering, withLingering } from './linger.js';
import { touchOnly } from '../../platform/pointer.js';
import { listName, mergeList, moveList, renameList } from './structureActions.js';
import { areaPicker } from '../areas/areaActions.js';
import { archiveTask, createTask, createTasks, moveTask, rowKey, setCompleted, setPinned, updateTask } from './taskActions.js';
import { toast } from '../../platform/toast.js';
import { maybeSeveral, openTaskImport } from '../import/store.js';

/** 0.63: Rückfrage nach dem Einfügen mehrerer Aufgaben – nachgeladen, samt Leser */
const PastePrompt = lazy(() => import('../import/PastePrompt.js').then((m) => ({ default: m.PastePrompt })));
import { createSection, dissolveSection, moveSection, renameSection } from './detailActions.js';
import { selection, useSelected, useSelectionMode } from './selection.js';
import { overviewEnabled, setTaskPinned } from './projectActions.js';

/**
 * Aufgabenlisten (Pakete P2/P4): erfassen per Enter und Einfügen, abhaken, Titel direkt bearbeiten, Termin und
 * Zuständige direkt an der Zeile, Reihenfolge per Ziehen (auch zwischen Listen), per Tastatur (Alt+Pfeil) und per
 * Menü – jede Ziehaktion hat einen Weg ohne Ziehen. Tastatur: eine Zeile ist fokussierbar (roving tabindex).
 */

/** Maus sofort (ab 6 px), Touch nach langem Drücken – kollidiert nicht mit dem Scrollen */
export function useDragSensors() {
  return useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }));
}

/**
 * Ende eines Ziehvorgangs in der Listenansicht: über einer Aufgabe → an ihre Stelle (auch in einer anderen Liste oder
 * einem anderen Abschnitt), über einer (leeren) Liste bzw. einem Abschnitt → ans Ende. Positionen rechnet `moveTask`
 * aus den Nachbarn.
 */
export function handleListDragEnd(e: DragEndEvent, tasks: TaskDto[], sections: SectionDto[] = []) {
  noteDragEnd();
  if (!e.over || e.active.id === e.over.id) return;
  const task = tasks.find((x) => x.id === e.active.id);
  if (!task) return;
  const overTask = tasks.find((x) => x.id === e.over!.id);
  const data = e.over.data.current as { listId?: string; sectionId?: string | null } | undefined;
  const targetListId = overTask?.listId ?? data?.listId;
  if (!targetListId) return;
  const known = new Set(sections.map((x) => x.id));
  const sectionOf = (x: TaskDto) => (x.sectionId && known.has(x.sectionId) ? x.sectionId : null);
  const targetSection = overTask ? sectionOf(overTask) : (data?.sectionId ?? null);
  const target = tasks.filter((x) => x.listId === targetListId && sectionOf(x) === targetSection);
  // AF12: vorherige Nachbarn merken – „Rückgängig“ stellt die alte Stelle wieder her, solange sie noch gilt
  const before = neighbours(tasks.filter((x) => x.listId === task.listId && sectionOf(x) === sectionOf(task)), task);
  const move = (prev: TaskDto | null, next: TaskDto | null) => void loadUndo().then((m) => m.moveTaskUndoable(task, targetListId, prev, next, targetSection, before, t('feedback.reordered', { title: task.title })));
  if (targetListId === task.listId && sectionOf(task) === targetSection) {
    if (!overTask) return;
    const ordered = arrayMove(target, target.indexOf(task), target.indexOf(overTask));
    const i = ordered.indexOf(task);
    move(ordered[i - 1] ?? null, ordered[i + 1] ?? null);
    return;
  }
  const rest = target.filter((x) => x.id !== task.id);
  if (!overTask) return move(rest.at(-1) ?? null, null);
  const i = rest.indexOf(overTask);
  move(rest[i - 1] ?? null, overTask);
}

/** Rückmeldung und Rückgängig (AF11/AF12) liegen nachgeladen; ProjectPage lädt sie im Leerlauf vor */
export const loadUndo = () => import('./taskUndo.js');

/** Nachbarn einer Aufgabe in ihrer Gruppe (für den Rückweg beim Verschieben) */
function neighbours(group: TaskDto[], task: TaskDto) {
  const i = group.findIndex((x) => x.id === task.id);
  return { prevId: group[i - 1]?.id ?? null, nextId: group[i + 1]?.id ?? null };
}

export interface ListSectionProps {
  projectId: string;
  list: TaskListDto;
  lists: TaskListDto[];
  /** Abschnitte dieser Liste (Paket P8), nach Position */
  sections: SectionDto[];
  labels: LabelDto[];
  showHeading: boolean;
  /** Projekt hat Bereiche (P14): Liste lässt sich einem Bereich zuordnen */
  hasAreas?: boolean;
  tasks: TaskDto[];
  members: ProjectMemberDto[];
  autoFocus: boolean;
  /** Fortschritt (Paket P8): erledigt / gesamt laut Snapshot */
  doneCount?: number;
  totalCount?: number;
  /** „Abschnitt hinzufügen“ wurde außerhalb der Liste gewählt (eine einzige, unsichtbare Liste) */
  addingSection?: boolean;
  onSectionAddDone?: () => void;
  onOpen: DetailOpen;
  /** Etappe D (Leistung): erste Stufe – Zeilen ohne Zieh-Registrierung, die kommt im nächsten Schritt */
  staged?: boolean;
}

/**
 * Handlungen einer Zeile – EIN stabiles Objekt je Liste, damit Zeilen per `memo` nur neu zeichnen, wenn sich ihre
 * Aufgabe ändert (P5-Messung: sonst zeichnet jede Eingabe alle Zeilen neu). Aktueller Stand kommt über Refs.
 */
export interface RowActions {
  edit(id: string): void;
  editDone(id: string): void;
  focus(id: string): void;
  keyDown(e: KeyboardEvent<HTMLLIElement>, task: TaskDto): void;
  open: DetailOpen;
  move(task: TaskDto, to: number): void;
  moveToList(task: TaskDto, list: TaskListDto): void;
  picker(id: string, kind: PickerKind | null): void;
  position(task: TaskDto): { index: number; count: number };
  /** Abschnitte der Liste (für „In Abschnitt verschieben“) */
  sections(): SectionDto[];
  moveToSection(task: TaskDto, sectionId: string | null): void;
}

/** Aufgaben einer Liste nach Abschnitt: erst ohne Abschnitt, dann je Abschnitt in dessen Reihenfolge */
function groupBySection(tasks: TaskDto[], sections: SectionDto[]) {
  const known = new Set(sections.map((x) => x.id));
  const loose: TaskDto[] = [];
  const by = new Map<string, TaskDto[]>(sections.map((x) => [x.id, []]));
  for (const x of tasks) (x.sectionId && known.has(x.sectionId) ? by.get(x.sectionId)! : loose).push(x);
  return { loose, by };
}

export const ListSection = memo(function ListSection(props: ListSectionProps) {
  const rights = useRights();
  const rightsRef = useRef(rights);
  rightsRef.current = rights;
  const { projectId, list, sections } = props;
  // AF02: eben abgehakte Aufgaben bleiben stehen, bis der Zeiger die Liste verlässt
  const lingering = useLingering(list.id);
  const tasks = useMemo(() => withLingering(props.tasks, lingering), [props.tasks, lingering]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ id: string; kind: PickerKind } | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const root = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const pendingList = list.id.startsWith('tmp-');
  const { loose, by } = useMemo(() => groupBySection(tasks, sections), [tasks, sections]);
  // Anzeige-Reihenfolge (für Tastatur und Verschieben): ohne Abschnitt, dann die Abschnitte
  const display = useMemo(() => (sections.length ? [...loose, ...sections.flatMap((x) => by.get(x.id) ?? [])] : tasks), [tasks, sections, loose, by]);
  const lastLooseId = loose.at(-1)?.id;
  // Leere Listen sind Ablageziel beim Ziehen zwischen Listen
  const drop = useDroppable({ id: `list:${list.id}`, data: { listId: list.id, sectionId: null }, disabled: pendingList });
  const latest = useRef({ tasks: display, list, sections, onOpen: props.onOpen });
  latest.current = { tasks: display, list, sections, onOpen: props.onOpen };

  // Fokus nach Verschieben/Löschen auf die richtige Zeile setzen
  useEffect(() => {
    if (!focusId) return;
    const el = root.current?.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(focusId)}"]`);
    if (el) {
      el.focus();
      setFocusId(null);
    }
  }, [focusId, tasks]);

  const tabbableId = activeId && tasks.some((x) => x.id === activeId) ? activeId : display[0]?.id;
  // Gleiche IDs → gleiches Array: sonst zeichnet dnd-kit bei jeder Änderung alle Zeilen der Liste neu
  const idKey = display.map((x) => x.id).join(',');
  const itemIds = useMemo(() => (idKey ? idKey.split(',') : []), [idKey]);

  const actions = useMemo<RowActions>(() => {
    const known = () => new Set(latest.current.sections.map((x) => x.id));
    const sectionOf = (x: TaskDto | undefined) => (x?.sectionId && known().has(x.sectionId) ? x.sectionId : null);
    /**
     * Verschieben an einen Index der Anzeige (Ziehen, Tastatur, Menü teilen sich diesen Weg). Über eine
     * Abschnittsgrenze hinweg wechselt die Aufgabe in den Abschnitt ihres neuen Vorgängers.
     */
    const moveToIndex = (task: TaskDto, to: number) => {
      const { tasks, list } = latest.current;
      const from = tasks.findIndex((x) => x.id === task.id);
      if (from < 0 || to < 0 || to >= tasks.length || from === to) return;
      const ordered = arrayMove(tasks, from, to);
      const prev = ordered[to - 1];
      const next = ordered[to + 1];
      // Nach unten: Abschnitt des übersprungenen Vorgängers; nach oben: des übersprungenen Nachfolgers
      const section = to > from ? sectionOf(prev) : sectionOf(next);
      void moveTask(task, list.id, prev && sectionOf(prev) === section ? prev : null, next && sectionOf(next) === section ? next : null, section);
      setFocusId(task.id);
    };
    return {
      edit: (id) => setEditingId(id),
      editDone: (id) => {
        setEditingId(null);
        setFocusId(id);
      },
      focus: (id) => setActiveId(id),
      open: (id, tab) => {
        release([id]);
        latest.current.onOpen(id, tab);
      },
      move: moveToIndex,
      moveToList: (task, l) =>
        void loadUndo().then((m) => m.moveTaskUndoable(task, l.id, null, null, undefined, neighbours(latest.current.tasks.filter((x) => x.sectionId === task.sectionId), task), t('feedback.moved', { title: task.title, target: listName(l) }))),
      picker: (id, kind) => {
        setPicker(kind ? { id, kind } : null);
        if (!kind) setFocusId(id);
      },
      position: (task) => ({ index: latest.current.tasks.findIndex((x) => x.id === task.id), count: latest.current.tasks.length }),
      sections: () => latest.current.sections,
      moveToSection: (task, sectionId) => {
        const target = latest.current.tasks.filter((x) => x.id !== task.id && sectionOf(x) === sectionId);
        void moveTask(task, latest.current.list.id, target.at(-1) ?? null, null, sectionId);
      },
      keyDown: (e, task) => {
        if (e.target !== e.currentTarget) return;
        const { tasks } = latest.current;
        const index = tasks.findIndex((x) => x.id === task.id);
        const go = (i: number) => {
          const target = tasks[Math.max(0, Math.min(tasks.length - 1, i))];
          if (target) {
            setActiveId(target.id);
            setFocusId(target.id);
          }
          return target;
        };
        const key = e.key;
        const pending = task.id.startsWith('tmp-');
        // Gäste (P15): ohne „Bearbeiten“ nur ansehen und vormerken (mit „Ausführen“ auch erledigen); löschen nie
        const r = rightsRef.current;
        if (!r.edit && (e.altKey || e.shiftKey || ![...['ArrowDown', 'ArrowUp', 'j', 'k', 'Home', 'End', 'Enter', 'h'], ...(r.execute ? [' ', 'x'] : [])].includes(key))) return;
        if (!r.remove && (key === 'Delete' || key === 'Backspace')) return;
        let handled = true;
        if (e.altKey && key === 'ArrowUp') moveToIndex(task, index - 1);
        else if (e.altKey && key === 'ArrowDown') moveToIndex(task, index + 1);
        else if (e.shiftKey && (key === 'ArrowDown' || key === 'ArrowUp')) {
          // Auswahl per Tastatur erweitern (Paket P8)
          const target = go(index + (key === 'ArrowDown' ? 1 : -1));
          if (!pending) selection.add(task.id, ...(target && !target.id.startsWith('tmp-') ? [target.id] : []));
        } else if (key === 'ArrowDown' || key === 'j') go(index + 1);
        else if (key === 'ArrowUp' || key === 'k') go(index - 1);
        else if (key === 'Home') go(0);
        else if (key === 'End') go(tasks.length - 1);
        else if (pending) handled = false;
        else if (key === ' ' || key === 'x') {
          // Erledigt verschwindet aus der offenen Liste: Fokus auf die Nachbarzeile, damit die Tastatur weiterarbeitet
          if (!task.completedAt) go(index + 1 < tasks.length ? index + 1 : index - 1);
          void setCompleted(task, !task.completedAt).catch(() => {});
        } else if (key === 'Enter') latest.current.onOpen(task.id);
        else if (key === 'F2' || key === 'e') setEditingId(task.id);
        else if (key === 'f') setPicker({ id: task.id, kind: 'due' });
        else if (key === 'z') setPicker({ id: task.id, kind: 'people' });
        else if (key === 'p') setPicker({ id: task.id, kind: 'priority' });
        else if (key === 'l') setPicker({ id: task.id, kind: 'labels' });
        // AF04/AF05: kleine Arbeitsräume per Tastatur
        // Abnahme 0.49 F04: auch ohne vorhandene Einträge – dann öffnet das kleine Fenster mit leerem Eingabefeld
        else if (key === 'u' && !task.parentId) setPicker({ id: task.id, kind: 'subtasks' });
        else if (key === 'c') setPicker({ id: task.id, kind: 'checklist' });
        else if (key === 'h') pinForToday(task);
        else if (key === 'Delete' || key === 'Backspace') {
          go(index + 1 < tasks.length ? index + 1 : index - 1);
          void archiveTask(task);
        } else if (key === 'n') input.current?.focus();
        else handled = false;
        if (handled) e.preventDefault();
      },
    };
  }, []);

  const rows = (items: TaskDto[]) =>
    items.map((task) => (
      <TaskRow
        key={rowKey(task.id)}
        task={task}
        members={props.members}
        labels={props.labels}
        lists={props.lists}
        currentList={list}
        tabbable={task.id === tabbableId}
        lingering={lingering.includes(task)}
        editing={editingId === task.id}
        picker={picker?.id === task.id ? picker.kind : null}
        actions={actions}
        staged={props.staged}
      />
    ));

  const showAddSection = addingSection || !!props.addingSection;
  const endAddSection = () => {
    setAddingSection(false);
    props.onSectionAddDone?.();
  };
  // Gesamtabnahme 0.58: wer nichts anlegen darf, sieht keine leere Liste (leere Strukturen nicht anzeigen)
  if (!rights.edit && props.showHeading && props.tasks.length === 0 && !(props.doneCount ?? 0) && !sections.length) return null;

  return (
    <section
      ref={root}
      aria-label={props.showHeading ? listName(list) : t('list.default')}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse' && lingering.length) release(lingering.map((x) => x.id));
      }}
    >
      {props.showHeading && <ListHeading list={list} lists={props.lists} hasAreas={props.hasAreas} openCount={props.tasks.length} done={props.doneCount ?? 0} total={props.totalCount ?? 0} onAddSection={() => setAddingSection(true)} />}
      {props.tasks.length === 0 && !props.showHeading && !sections.length && <p className="mb-3 text-sm text-zinc-500">{t('task.empty')}</p>}
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <ul
          ref={drop.setNodeRef}
          className={`divide-y divide-zinc-100 rounded-lg dark:divide-zinc-800 ${loose.length === 0 && (props.showHeading || sections.length) ? 'min-h-6' : ''} ${drop.isOver ? 'bg-zinc-100 dark:bg-zinc-900' : ''}`}
          aria-describedby="task-keys"
        >
          {rows(loose)}
        </ul>
        {pendingList ? (
          <p className="px-3 py-2 text-sm text-zinc-500">{t('list.pending')}</p>
        ) : (
          rights.edit && <AddTaskInput projectId={projectId} listId={list.id} inputRef={input} autoFocus={props.autoFocus} onArrowUp={() => lastLooseId && setFocusId(lastLooseId)} />
        )}
        {sections.map((sec, i) => (
          <SectionBlock key={sec.id} projectId={projectId} list={list} section={sec} sections={sections} index={i} tasks={by.get(sec.id) ?? NO_TASKS} onFocusRow={setFocusId}>
            {rows(by.get(sec.id) ?? NO_TASKS)}
          </SectionBlock>
        ))}
      </SortableContext>
      {showAddSection && (
        <div className="mt-3 flex">
          <InlineName
            value=""
            label={t('section.namePlaceholder')}
            onDone={(name) => {
              endAddSection();
              if (name) void createSection(projectId, list.id, name).catch(() => {});
            }}
          />
        </div>
      )}
    </section>
  );
});

const NO_TASKS: TaskDto[] = [];

/**
 * Abschnitt in einer Liste (Paket P8): Überschrift (antippen benennt um), Menü (verschieben, auflösen), eigene
 * Eingabezeile. Leere Abschnitte sind Ablageziel beim Ziehen. Auflösen ist der Rückweg: Aufgaben bleiben.
 */
function SectionBlock(p: { projectId: string; list: TaskListDto; section: SectionDto; sections: SectionDto[]; index: number; tasks: TaskDto[]; onFocusRow: (id: string) => void; children: ReactNode }) {
  const rights = useRights();
  const { section, sections, index } = p;
  const [editing, setEditing] = useState(false);
  const drop = useDroppable({ id: `section:${section.id}`, data: { listId: p.list.id, sectionId: section.id } });
  const lastId = p.tasks.at(-1)?.id;
  return (
    <div role="group" aria-label={section.name} className="mt-4">
      <div className="flex items-center gap-1 border-b border-zinc-200 pb-0.5 dark:border-zinc-800">
        {editing ? (
          <InlineName
            value={section.name}
            label={t('section.nameLabel', { name: section.name })}
            className="text-sm font-semibold"
            onDone={(name) => {
              setEditing(false);
              if (name && name !== section.name) renameSection(p.projectId, section, name);
            }}
          />
        ) : (
          <h3 className="min-w-0 flex-1">
            <button type="button" disabled={!rights.manage} onClick={() => setEditing(true)} className="max-w-full truncate rounded-lg px-1 text-left text-sm font-semibold hover:bg-zinc-100 disabled:hover:bg-transparent dark:hover:bg-zinc-800">
              {section.name}
            </button>
            <span className="ml-1 text-xs text-zinc-500">{p.tasks.length || ''}</span>
          </h3>
        )}
        {rights.manage && <Menu small label={t('section.menu', { name: section.name })} trigger={<MoreHorizontal className="size-4" aria-hidden />}>
          <MenuItem onSelect={() => setEditing(true)}>{t('section.rename')}</MenuItem>
          <MenuItem disabled={index === 0} onSelect={() => moveSection(p.projectId, section, sections, index - 1)}>
            {t('section.up')}
          </MenuItem>
          <MenuItem disabled={index >= sections.length - 1} onSelect={() => moveSection(p.projectId, section, sections, index + 1)}>
            {t('section.down')}
          </MenuItem>
          <MenuSeparator />
          <MenuItem onSelect={() => dissolveSection(p.projectId, section)}>{t('section.dissolve')}</MenuItem>
        </Menu>}
      </div>
      <ul ref={drop.setNodeRef} className={`min-h-6 divide-y divide-zinc-100 rounded-lg dark:divide-zinc-800 ${drop.isOver ? 'bg-zinc-100 dark:bg-zinc-900' : ''}`}>
        {p.children}
      </ul>
      {rights.edit && <AddTaskInput
        projectId={p.projectId}
        listId={p.list.id}
        sectionId={section.id}
        draftKey={`add.${p.list.id}.${section.id}`}
        placeholder={t('section.addTask', { name: section.name })}
        onArrowUp={() => lastId && p.onFocusRow(lastId)}
      />}
    </div>
  );
}

/**
 * Überschrift einer Liste – nur sichtbar, wenn es mehr als eine gibt (Regel R6). Name antippen benennt um; das Menü
 * sortiert und führt zusammen (Rückweg für „Liste hinzufügen“, mit Rückfrage). Rechts der Fortschritt (Paket P8).
 */
function ListHeading(p: { list: TaskListDto; lists: TaskListDto[]; hasAreas?: boolean; openCount: number; done: number; total: number; onAddSection: () => void }) {
  const { list, lists, openCount, onAddSection } = p;
  const rights = useRights();
  const [editing, setEditing] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<TaskListDto | null>(null);
  const [merging, setMerging] = useState(false);
  const index = lists.findIndex((l) => l.id === list.id);
  const pending = list.id.startsWith('tmp-');
  return (
    <div className="mb-1 flex items-center gap-1">
      {editing ? (
        <InlineName
          value={listName(list)}
          label={t('list.nameLabel', { name: listName(list) })}
          className="text-lg font-semibold"
          onDone={(name) => {
            setEditing(false);
            if (name && name !== list.name) renameList(list, name);
          }}
        />
      ) : (
        <h2 className="min-w-0 flex-1">
          <button type="button" disabled={pending || !rights.manage} onClick={() => setEditing(true)} className="max-w-full truncate rounded-lg px-1 text-left text-lg font-semibold hover:bg-zinc-100 disabled:hover:bg-transparent dark:hover:bg-zinc-800">
            {listName(list)}
          </button>
        </h2>
      )}
      <ListProgress done={p.done} total={p.total} />
      {!pending && rights.manage && (
        <Menu label={t('list.menu', { name: listName(list) })} trigger={<MoreHorizontal className="size-5" aria-hidden />}>
          {/* 0.150 (ADR 0152): 1 Bearbeiten · 2 Organisieren · 5 Entfernen (Zusammenführen löst die Liste auf) */}
          <MenuGroups
            groups={[
              {
                key: 'open',
                items: (
                  <>
                    <MenuItem onSelect={() => setEditing(true)}>{t('list.rename')}</MenuItem>
                    <MenuItem onSelect={() => setTimeout(onAddSection, 0)}>{t('section.add')}</MenuItem>
                    <MenuItem onSelect={() => setTimeout(() => openTaskImport({ projectId: list.projectId, listId: list.id }), 0)}>{t('import.menu')}</MenuItem>
                  </>
                ),
              },
              {
                key: 'organize',
                items: (
                  <>
                    {openCount > 0 && <MenuItem onSelect={() => selection.enter()}>{t('select.enter')}</MenuItem>}
                    {p.hasAreas && (
                      <MenuItem onSelect={() => setTimeout(() => areaPicker.open({ kind: 'list', projectId: list.projectId, id: list.id, name: listName(list), areaId: list.areaId }), 0)}>{t('areas.assign')}</MenuItem>
                    )}
                    <MenuItem disabled={index <= 0} onSelect={() => moveList(list, lists, index - 1)}>
                      {t('list.up')}
                    </MenuItem>
                    <MenuItem disabled={index >= lists.length - 1} onSelect={() => moveList(list, lists, index + 1)}>
                      {t('list.down')}
                    </MenuItem>
                  </>
                ),
              },
              {
                key: 'remove',
                items: lists.some((l) => l.id !== list.id && !l.id.startsWith('tmp-')) && (
                  <MenuItem onSelect={() => setTimeout(() => setMerging(true), 0)} data-menu-action="merge">
                    {t('list.mergeMenu')}
                  </MenuItem>
                ),
              },
            ]}
          />
        </Menu>
      )}
      {merging && (
        <Suspense fallback={null}>
          <TargetPicker
            title={t('list.mergePick', { name: listName(list) })}
            options={lists.filter((l) => !l.id.startsWith('tmp-')).map((l) => ({ id: l.id, label: listName(l), current: l.id === list.id }))}
            onPick={(id) => setMergeTarget(lists.find((l) => l.id === id) ?? null)}
            onClose={() => setMerging(false)}
          />
        </Suspense>
      )}
      <ConfirmDialog
        open={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        title={t('list.mergeTitle')}
        confirm={t('list.mergeConfirm')}
        onConfirm={() => mergeTarget && mergeList(list, mergeTarget)}
      >
        <p>{mergeTarget && t('list.mergeText', { from: listName(list), to: listName(mergeTarget) })}</p>
        <p className="text-zinc-500">{t('board.cards', { n: openCount })}</p>
      </ConfirmDialog>
    </div>
  );
}

/** Fortschritt einer Liste: erledigt von gesamt (Zähler kommen mit dem Snapshot), nur wenn es Aufgaben gibt */
function ListProgress({ done, total }: { done: number; total: number }) {
  if (!total) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <span className="flex shrink-0 items-center gap-2 px-1 text-xs text-zinc-500" aria-label={t('list.progress', { done, n: total })} title={t('list.progress', { done, n: total })}>
      <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-zinc-200 sm:block dark:bg-zinc-800" aria-hidden>
        <span className="block h-full rounded-full bg-[var(--color-accent)]" style={{ width: `${pct}%` }} />
      </span>
      <span aria-hidden>
        {done}/{total}
      </span>
    </span>
  );
}

/** Aus dem Projekt heraus für Mein Tag vormerken (persönlich, für andere unsichtbar) */
function pinForToday(task: TaskDto) {
  setPinned(task, true);
  toast(t('myday.pinnedToast', { title: task.title }));
}

/** Name direkt bearbeiten: Enter/Verlassen speichert, Esc verwirft */
export function InlineName({ value, label, className, onDone }: { value: string; label: string; className?: string; onDone: (v: string | null) => void }) {
  const [v, setV] = useState(value);
  const finished = useRef(false);
  const finish = (result: string | null) => {
    if (finished.current) return;
    finished.current = true;
    onDone(result);
  };
  return (
    <input
      autoFocus
      value={v}
      aria-label={label}
      maxLength={120}
      onChange={(e) => setV(e.target.value)}
      onFocus={(e) => e.target.select()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) finish(v.trim() || null);
        else if (e.key === 'Escape') finish(null);
      }}
      onBlur={() => finish(v.trim() || null)}
      className={`min-h-10 min-w-0 flex-1 rounded-lg border border-zinc-300 bg-white px-2 dark:border-zinc-700 dark:bg-zinc-900 ${className ?? ''}`}
    />
  );
}

/**
 * Eingabefeld mit eigenem Zustand: Tippen zeichnet nur dieses Feld neu, nicht die ganze Liste.
 */
export function AddTaskInput(props: {
  projectId: string;
  listId: string;
  statusId?: string;
  completed?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  onArrowUp?: () => void;
  placeholder?: string;
  draftKey?: string;
  /** Abschnitt in der Liste (Paket P8) */
  sectionId?: string;
  /** Audit F04: vor dem ersten Anlegen die (noch fehlende) Liste anlegen – läuft im Ausgangskorb davor */
  ensureList?: () => void;
}) {
  // Entwurf: halb Getipptes überlebt Neuladen; abgelehnte Titel kommen hierher zurück
  const [draft, setDraft] = useDraftText(props.draftKey ?? `add.${props.listId}`);

  function submit(titles: string[]) {
    props.ensureList?.();
    // Fehler meldet der Ausgangskorb selbst und gibt den Text zurück ins Feld
    if (titles.length === 1 || props.statusId) {
      for (const title of titles) void createTask(props.projectId, props.listId, title, { statusId: props.statusId, completed: props.completed, sectionId: props.sectionId }).catch(() => {});
    } else void createTasks(props.projectId, props.listId, titles, props.sectionId).catch(() => {});
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowUp' && !draft && props.onArrowUp) {
      e.preventDefault();
      props.onArrowUp();
      return;
    }
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const title = draft.trim();
    if (!title) return;
    setDraft(''); // sofort leeren: weiterschreiben ohne Warten
    submit([title]);
  }

  /**
   * 0.63 (ADR 0057): Mehrere Zeilen eingefügt → erst nachfragen („n Aufgaben anlegen“, „Prüfen …“, „Als eine
   * Aufgabe“). Labels, Termine und Checklisten im Text werden dabei erkannt.
   */
  const [pasted, setPasted] = useState<string | null>(null);
  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    if (draft.trim()) return; // in angefangenen Text normal einfügen
    const text = e.clipboardData.getData('text');
    if (!maybeSeveral(text)) return;
    e.preventDefault();
    setPasted(text);
  }

  const label = props.placeholder ?? t('task.add');
  const hintId = `hint-${props.listId}-${props.statusId ?? props.sectionId ?? 'l'}`;
  return (
    <>
      <input
        ref={props.inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={label}
        aria-label={label}
        aria-describedby={hintId}
        autoFocus={props.autoFocus}
        maxLength={TASK_TITLE_MAX}
        className="mt-1 min-h-11 w-full rounded-lg border border-transparent bg-transparent px-3 text-sm placeholder:text-zinc-400 hover:border-zinc-200 focus:border-zinc-300 focus:bg-white dark:hover:border-zinc-800 dark:focus:border-zinc-700 dark:focus:bg-zinc-900"
      />
      <p id={hintId} className="sr-only">
        {t('task.addHint')}
      </p>
      {pasted !== null && (
        <Suspense fallback={null}>
          <PastePrompt
            projectId={props.projectId}
            listId={props.listId}
            statusId={props.statusId}
            sectionId={props.sectionId}
            text={pasted}
            ensureList={props.ensureList}
            onDone={() => setPasted(null)}
            onSingle={(title) => {
              setPasted(null);
              submit([title.slice(0, TASK_TITLE_MAX)]);
            }}
            onCancel={() => {
              // Zurück ins Feld (ein Eingabefeld hat keine Zeilen: Zeilen werden zu „ · “)
              setDraft(pasted.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).join(' · '));
              setPasted(null);
              props.inputRef?.current?.focus();
            }}
          />
        </Suspense>
      )}
    </>
  );
}

export interface RowProps {
  task: TaskDto;
  members: ProjectMemberDto[];
  labels?: LabelDto[];
  lists: TaskListDto[];
  currentList: TaskListDto;
  tabbable: boolean;
  editing: boolean;
  picker: PickerKind | null;
  actions: RowActions;
  /** Erledigte: keine Reihenfolge, kein Ziehen */
  fixedOrder?: boolean;
  /** AF02: eben erledigt, bleibt kurz an seinem Platz */
  lingering?: boolean;
  /** Etappe D: ohne `useSortable` (erste Stufe des Aufbaus) */
  staged?: boolean;
}

type SortableBits = Pick<ReturnType<typeof useSortable>, 'setNodeRef' | 'listeners' | 'transform' | 'transition' | 'isDragging'>;
const NO_SORT: SortableBits = { setNodeRef: () => {}, listeners: undefined, transform: null, transition: undefined, isDragging: false };

/**
 * Etappe D (Leistung am Handy): `useSortable` kostet je Zeile spürbar Zeit (Registrierung bei dnd-kit). Die erste,
 * sichtbare Stufe einer Liste baut Zeilen ohne Zieh-Registrierung; im nächsten, unterbrechbaren Schritt kommen alle
 * Zeilen mit Registrierung. Ziehen ist so erst nach dem ersten Bild möglich – wenige Millisekunden später.
 */
export const TaskRow = memo(function TaskRow(p: RowProps) {
  return p.staged ? <RowShell p={p} sort={NO_SORT} draggable={false} /> : <SortableRow {...p} />;
});

function SortableRow(p: RowProps) {
  const rights = useRights();
  const still = p.task.id.startsWith('tmp-') || p.editing || p.fixedOrder || p.lingering || !rights.edit;
  const sort = useSortable({ id: p.task.id, data: { listId: p.task.listId }, disabled: still });
  return <RowShell p={p} sort={sort} draggable={!still} />;
}

/** Reines Touchgerät? Einmal je Sitzung bestimmen (nicht je Zeile `matchMedia` aufrufen) */
let touchCache: boolean | null = null;
const isTouch = () => (touchCache ??= touchOnly());

/** Nach einem Ziehvorgang keinen Klick auswerten (AF02: „Ein Drag öffnet nach dem Loslassen kein Detailfenster“) */
let draggedAt = 0;
export const noteDragEnd = () => {
  draggedAt = Date.now();
};
/** Klick auf eine freie Fläche der Zeile (nicht auf Knöpfe, Felder, Links, Menüs) */
const isFreeArea = (target: EventTarget | null, row: HTMLElement) => {
  const el = target as HTMLElement | null;
  return !!el && row.contains(el) && !el.closest('button, a, input, textarea, select, label, [role="menu"], [role="menuitem"], [role="dialog"]');
};

function RowShell({ p, sort: sortable, draggable }: { p: RowProps; sort: SortableBits; draggable: boolean }) {
  const { task, actions } = p;
  const rights = useRights();
  const pending = task.id.startsWith('tmp-');
  const done = !!task.completedAt;
  const still = !draggable;
  const style = { transform: DndCSS.Transform.toString(sortable.transform), transition: sortable.transition };
  const selected = useSelected(task.id);
  // U02: im Auswahlmodus trägt jede Zeile ein eigenes Auswahlkästchen (getrennt vom Erledigt-Haken)
  const selecting = useSelectionMode() && !pending && !p.fixedOrder && !rights.guest;
  // Die Zeile zeichnet bei jeder Listenänderung neu (dnd-kit) – Beschriftung nur bei geändertem Titel neu bilden
  const label = useMemo(() => t('task.row', { title: task.title, state: done ? t('task.rowDone') : '' }), [task.title, done]);
  // AF02: am Desktop zieht nur der Griff (Titel und Fläche öffnen, Text lässt sich markieren); am Touchgerät die
  // ganze Zeile nach langem Drücken – ohne Hover gäbe es den Griff sonst nicht
  const touch = isTouch();
  // Leistung: den Griff erst zeigen, wenn die Zeile „heiß“ ist (Hover/Fokus) – nicht 200 Knöpfe auf Vorrat
  const [hot, setHot] = useState(false);

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      {...(touch ? sortable.listeners : {})}
      data-task-id={task.id}
      tabIndex={p.tabbable ? 0 : -1}
      onFocus={(e) => {
        if (!touch) setHot(true);
        if (e.target === e.currentTarget) actions.focus(task.id);
      }}
      onPointerEnter={touch ? undefined : () => setHot(true)}
      onKeyDown={(e) => actions.keyDown(e, task)}
      // Mehrfachauswahl (Paket P8): Strg/⌘+Klick, Umschalt+Klick, im Auswahlmodus jedes Antippen – hat Vorrang vor Öffnen
      onClickCapture={(e) => {
        if (pending || p.fixedOrder || rights.guest || !e.currentTarget.contains(e.target as Node)) return;
        const mod = e.ctrlKey || e.metaKey;
        if (!mod && !e.shiftKey && !selection.mode) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey && selection.size) selection.range(task.id);
        else selection.toggle(task.id);
      }}
      // AF02: freie Zeilenfläche öffnet wie der Titel – keine unsichtbare Fläche über anderen Knöpfen
      onClick={(e) => {
        if (pending || p.editing || Date.now() - draggedAt < 400 || !isFreeArea(e.target, e.currentTarget)) return;
        if (window.getSelection()?.toString()) return; // Text markiert: nicht öffnen
        actions.open(task.id);
      }}
      aria-label={label}
      aria-selected={selected || undefined}
      className={`group relative flex cursor-pointer items-center gap-1 rounded-lg py-0.5 outline-none transition-colors duration-[var(--motion-hover)] focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] motion-reduce:transition-none ${
        sortable.isDragging ? 'z-10 bg-white shadow-lg dark:bg-zinc-900' : selected ? 'bg-[var(--color-accent)]/10 shadow-[inset_3px_0_0_var(--color-accent)] dark:bg-[var(--color-accent)]/20' : 'hover:bg-zinc-50 focus-within:bg-zinc-50 focus-visible:bg-zinc-100 dark:hover:bg-zinc-900/60 dark:focus-within:bg-zinc-900/60 dark:focus-visible:bg-zinc-900'
      } ${p.lingering ? 'opacity-70' : ''} ${pending ? 'row-in' : ''}`}
    >
      {/* AF02: Griff zum Ziehen – erscheint bei Hover/Fokus links außerhalb der Zeile, verschiebt nichts */}
      {!touch && !still && (hot || sortable.isDragging) && (
        <button
          type="button"
          tabIndex={-1}
          {...sortable.listeners}
          aria-label={t('task.dragHandle', { title: task.title })}
          title={t('task.dragHint')}
          className="absolute top-1/2 -left-5 grid h-9 w-5 -translate-y-1/2 cursor-grab place-items-center rounded text-zinc-400 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-zinc-700 active:cursor-grabbing dark:hover:text-zinc-200"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      )}
      <AccentBar accent={task.accent} className="inset-y-1.5 left-0 w-[3px] rounded-full" />
      {selecting && (
        <button type="button" role="checkbox" aria-checked={selected} aria-label={t('select.row', { title: task.title })} data-select-box="" className="grid size-11 shrink-0 place-items-center rounded-lg">
          <span className={`grid size-5 place-items-center rounded-full border-2 ${selected ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white' : 'border-zinc-400 dark:border-zinc-500'}`}>
            {selected && <Check className="size-3.5" strokeWidth={3} aria-hidden />}
          </span>
        </button>
      )}
      <RowBody task={task} actions={actions} members={p.members} labels={p.labels} lists={p.lists} currentList={p.currentList} editing={p.editing} picker={p.picker} fixedOrder={p.fixedOrder} />
    </li>
  );
}

/**
 * Inhalt der Zeile (Leistung, Audit F13): `useSortable` zeichnet bei jeder Änderung der Liste alle Zeilen neu (Kontext
 * von dnd-kit). Der Inhalt hängt nur an stabilen Props und bleibt dabei unberührt – nur `<li>` mit Verschiebung ändert sich.
 */
const RowBody = memo(function RowBody(p: Omit<RowProps, 'tabbable'>) {
  const { task, actions } = p;
  const rights = useRights();
  const pending = task.id.startsWith('tmp-');
  const done = !!task.completedAt;
  // W04: Dichte, Titelumbruch und sichtbare Angaben der gewählten Darstellung
  const shown = useShown();
  const compact = shown.density === 'compact';
  const [moving, setMoving] = useState(false);
  return (
    <>
      {/* W02: ohne Bearbeitungsrecht nur Anzeige des Zustands, keine gesperrte Checkbox */}
      {!rights.execute && !pending ? <DoneMark task={task} className={compact ? 'size-11 pointer-fine:size-9' : 'size-11'} /> : (
      <label className={`flex size-11 shrink-0 cursor-pointer items-center justify-center ${compact ? 'pointer-fine:size-9' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          tabIndex={-1}
          checked={done}
          disabled={pending}
          onChange={(e) => {
            // AF13: Häkchen kurz bestätigen (nur bei eigener Bedienung, nicht beim Anzeigen)
            e.currentTarget.dataset.pop = '';
            // AF02: eben Erledigtes bleibt stehen (die nächste Checkbox rutscht nicht unter den Zeiger)
            if (!done) {
              if (!p.fixedOrder) linger(task);
              const r = setCompleted(task, true);
              trackDone(task.id, r);
              void r.catch(() => {});
            } else {
              release([task.id]);
              // Etappe D: eben Abgehaktes zurücknehmen – gegen den bestätigten Stand, nicht den Schnappschuss der Zeile
              void latestDone(task).then((fresh) => setCompleted(fresh, false)).catch(() => {});
            }
          }}
          aria-label={t('task.toggle', { title: task.title })}
          className="size-5 cursor-pointer accent-[var(--color-accent)]"
        />
      </label>
      )}
      {shown.props.has('cover') && task.cover && <TaskCover task={task} decorative className="size-8 shrink-0 rounded" />}
      {/* Audit F03: am Handy Titel zuerst (bis zwei Zeilen), Angaben darunter; ab sm in einer Zeile */}
      <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center">
        {p.editing ? (
          <TitleEditor task={task} onDone={() => actions.editDone(task.id)} />
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => actions.open(task.id)} // Audit 0.36 F06: Titel öffnet; Umbenennen über F2 oder Menü
            disabled={pending}
            data-task-title=""
            className={`min-h-11 min-w-0 text-left break-words ${compact ? 'pointer-fine:min-h-9' : ''} ${shown.wrap ? 'py-2 sm:py-2' : 'max-sm:line-clamp-2 max-sm:py-3 sm:truncate'} ${done ? 'text-zinc-500 line-through dark:text-zinc-400' : ''} ${pending ? 'opacity-60' : ''}`}
          >
            {task.title}
          </button>
        )}
        {/* freie Fläche (öffnet über die Zeile) */}
        <span className="hidden min-h-11 flex-1 sm:block" aria-hidden />
        {!pending && (
          <TaskMeta
            task={task}
            members={p.members}
            labels={p.labels}
            picker={p.picker}
            onPicker={(k) => (rights.edit || isPreview(k)) && actions.picker(task.id, k)}
            onOpen={actions.open}
            show={shown.props}
            className="flex items-center empty:hidden max-sm:-mt-2 max-sm:flex-wrap sm:shrink-0"
          />
        )}
      </div>
      {!pending && (
        <>
          <Menu label={t('task.menu', { title: task.title })} trigger={<MoreHorizontal className="size-5" aria-hidden />}>
            {() => (
              <Suspense fallback={<p className="px-3 py-2 text-sm text-zinc-500">{t('common.loading')}</p>}>
                <TaskRowMenuItems task={task} p={p} onMove={() => setTimeout(() => setMoving(true), 0)} onPin={() => pinForToday(task)} />
              </Suspense>
            )}
          </Menu>
          {moving && (
            <Suspense fallback={null}>
            <TargetPicker
              title={t('task.moveTitle', { title: task.title })}
              options={p.lists
                .filter((l) => !l.id.startsWith('tmp-'))
                .flatMap((l) => {
                  const here = l.id === p.currentList.id;
                  const own = here ? actions.sections() : [];
                  return [
                    { id: `l:${l.id}`, label: listName(l), depth: 0, current: here },
                    ...(own.length
                      ? [
                          { id: 's:', label: t('task.noSection'), depth: 1, hint: listName(l), current: !task.sectionId },
                          ...own.map((x) => ({ id: `s:${x.id}`, label: x.name, depth: 1, hint: listName(l), current: x.id === task.sectionId })),
                        ]
                      : []),
                  ];
                })}
              onPick={(id) => {
                if (id.startsWith('s:')) actions.moveToSection(task, id.slice(2) || null);
                else {
                  const l = p.lists.find((x) => `l:${x.id}` === id);
                  if (l && l.id !== p.currentList.id) actions.moveToList(task, l);
                }
              }}
              onClose={() => setMoving(false)}
            />
            </Suspense>
          )}
          <button
            type="button"
            tabIndex={-1}
            onClick={() => actions.open(task.id)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={t('task.open')}
            className="grid size-11 shrink-0 place-items-center rounded-lg text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <ChevronRight className="size-5" aria-hidden />
          </button>
        </>
      )}
    </>
  );
});

/**
 * Titel direkt in der Zeile: Enter speichert, Esc verwirft. Gespeichert wird über den Ausgangskorb – die Zeile ist
 * sofort wieder frei. Bei einem echten Konflikt bietet der Hinweis „Meine Fassung übernehmen“ an.
 */
function TitleEditor({ task, onDone }: { task: TaskDto; onDone: () => void }) {
  const [value, setValue] = useState(task.title);
  const finished = useRef(false);

  function save() {
    if (finished.current) return;
    finished.current = true;
    const title = value.trim();
    if (title && title !== task.title) void updateTask(task, { title }, { title }).catch(() => {});
    onDone();
  }

  return (
    <span className="min-w-0 flex-1">
      <input
        autoFocus
        value={value}
        maxLength={TASK_TITLE_MAX}
        aria-label={t('task.editLabel', { title: task.title })}
        onChange={(e) => setValue(e.target.value)}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finished.current = true;
            onDone();
          }
        }}
        onBlur={save}
        className="min-h-10 w-full rounded-lg border border-zinc-300 bg-white px-2 dark:border-zinc-700 dark:bg-zinc-900"
      />
    </span>
  );
}
